import { useState, useEffect } from "react";

export interface LoopsPost {
  url: string;
  content: string; // HTML string containing video
  published: string;
  title: string;
}

const FEED_URL = "https://loops.video/feeds/79172607395631104.atom";
const CORS_PROXY = "https://api.codetabs.com/v1/proxy/?quest=";

export function useLoopsPost() {
  const [post, setPost] = useState<LoopsPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`${CORS_PROXY}${encodeURIComponent(FEED_URL)}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((xmlText) => {
        if (cancelled) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(xmlText, "application/xml");

        const entry = doc.querySelector("entry");
        if (!entry) {
          setError("No loops found");
          return;
        }

        const title = entry.querySelector("title")?.textContent || "";
        const links = Array.from(entry.querySelectorAll("link"));
        
        const alternateLink = links.find((l) => l.getAttribute("rel") === "alternate") || links.find((l) => !l.getAttribute("rel"));
        const url = alternateLink?.getAttribute("href") || "https://loops.video/@elias";
        const published = entry.querySelector("published")?.textContent || "";
        
        let contentHtml = entry.querySelector("content")?.textContent || "";
        
        const enclosureLink = links.find((l) => l.getAttribute("rel") === "enclosure");
        const videoUrl = enclosureLink?.getAttribute("href") || "";
        
        // Find thumbnail in any namespace
        let posterUrl = "";
        const thumbnails = entry.getElementsByTagNameNS("*", "thumbnail");
        if (thumbnails.length > 0) {
          posterUrl = thumbnails[0].getAttribute("url") || "";
        } else {
          // Fallback search
          const mediaContent = entry.getElementsByTagNameNS("*", "content");
          if (mediaContent.length > 0) {
            posterUrl = mediaContent[0].getAttribute("url") || ""; // might have video but no thumbnail, fallback to something
          }
        }

        if (videoUrl) {
          contentHtml += `<div style="margin-top: 0.5rem;"><video src="${videoUrl}" controls poster="${posterUrl}" style="width: 100%; border-radius: 8px;"></video></div>`;
        }

        setPost({
          url,
          content: contentHtml,
          published,
          title,
        });
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? "Failed to load loops");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { post, loading, error };
}
